import React, { useState } from 'react';
import {
    Modal, Table, Input, Select, Button, Tabs, Dropdown
} from 'antd';
import {
    ShoppingBag, Paperclip, Smile, Send, ChevronDown,
    Minus, Plus, ArrowLeft,
    CheckCircle, Link as LinkIcon, FileSignature, FileText, QrCode, FileDown, Settings, Trash2, Pencil
} from 'lucide-react';
import Layout from '../../Layout';
import { useNavigate } from 'react-router-dom';

// ==========================================
// 1. DỮ LIỆU MẪU (MOCK DATA)
// ==========================================
const assetTableData = [
    { key: '1', maTS: 'TS.245', taiSan: 'Tivi [NTHQ]', thuHoiTu: 'BBBG_113', nguoiSuDung: 'Lê Quang Hưng', datCoc: 'Chưa trả', ngayThuHoi: '00:30 12/08/2026', soLuong: 2, tinhTrang: 'Đang sử dụng' }
];

const assetTableColumns = [
    { title: 'Mã TS', dataIndex: 'maTS', key: 'maTS' },
    { title: 'Tài sản', dataIndex: 'taiSan', key: 'taiSan' },
    { title: 'Thu hồi từ', dataIndex: 'thuHoiTu', key: 'thuHoiTu' },
    { title: 'Người sử dụng', dataIndex: 'nguoiSuDung', key: 'nguoiSuDung' },
    { title: 'Đặt cọc', dataIndex: 'datCoc', key: 'datCoc' },
    { title: 'Ngày thu hồi', dataIndex: 'ngayThuHoi', key: 'ngayThuHoi' },
    { title: 'Số lượng', dataIndex: 'soLuong', key: 'soLuong' },
    { title: 'Tình trạng sử dụng', dataIndex: 'tinhTrang', key: 'tinhTrang' },
];

// ==========================================
// 2. COMPONENT CON HIỂN THỊ
// ==========================================
const InfoItem = ({ label, value }) => (
    <div className="mb-4">
        <p className="text-gray-400 dark:text-gray-500 text-[12px] mb-1">{label}</p>
        <p className="text-gray-800 dark:text-gray-200 text-[13px] font-medium">{value}</p>
    </div>
);

const CollapsibleSection = ({ title, extra, children, defaultExpanded = true, noPadding = false }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    return (
        <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300">
            <div
                className="flex justify-between items-center px-4 sm:px-5 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h3 className="text-[14px] font-bold text-gray-800 dark:text-gray-200">{title}</h3>
                <div className="flex items-center space-x-3 text-gray-400">
                    {extra && <div onClick={e => e.stopPropagation()} className="cursor-pointer hover:text-gray-600 transition-colors">{extra}</div>}
                    <button className="outline-none text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                        {isExpanded ? <Minus size={16} strokeWidth={1.5} /> : <Plus size={16} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <div className={`border-t border-gray-100 dark:border-gray-700 animate-fade-in ${noPadding ? '' : 'p-4 sm:p-5'}`}>
                    {children}
                </div>
            )}
        </div>
    );
};

// ==========================================
// 3. COMPONENT CHÍNH
// ==========================================
const RecallDetail = () => {
    const navigate = useNavigate();

    // State quản lý danh sách tin nhắn thảo luận và ô input
    const [discussions, setDiscussions] = useState([]);
    const [inputMessage, setInputMessage] = useState('');

    const handleSendMessage = () => {
        if (!inputMessage.trim()) return;
        const newMessage = {
            id: Date.now(),
            author: 'Admin Hệ Thống',
            time: 'Vừa xong',
            text: inputMessage.trim()
        };
        setDiscussions([...discussions, newMessage]);
        setInputMessage('');
    };

    const rightClickMenuItems = [
        { 
            key: 'sign', 
            icon: <FileSignature size={16} className="text-gray-500" />, 
            label: 'Tạo hồ sơ ký số',
            onClick: () => navigate('/submit-for-signature/add')
        },
        { key: 'link_object', icon: <LinkIcon size={16} className="text-gray-500" />, label: 'Liên kết đối tượng liên quan' },
        { type: 'divider' },
        { 
            key: 'form', 
            icon: <FileText size={16} className="text-gray-500" />, 
            label: 'Biểu mẫu',
            children: [
                { 
                    key: 'qrcode', 
                    label: 'Qrcode',
                    icon: <QrCode size={16} className="text-gray-500" />,
                    children: [
                        { key: 'export', label: 'Xuất file', icon: <FileDown size={16} className="text-gray-500" /> }
                    ]
                }
            ]
        },
        { 
            key: 'edit', 
            icon: <Pencil size={16} className="text-gray-500" />, 
            label: 'Sửa',
            onClick: () => navigate('/assets/edit')
        },
        { key: 'delete', icon: <Trash2 size={16} className="text-gray-500" />, label: 'Xóa' },
        { type: 'divider' },
        { key: 'customize', icon: <Settings size={16} className="text-gray-500" />, label: 'Tùy chỉnh' },
    ];

    return (
        <Layout>
            {/* TOOLBAR */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2.5 flex flex-col sm:flex-row justify-between items-start sm:items-center sticky top-[64px] z-10 shadow-sm gap-2 sm:gap-0">
                <div className="flex items-center">
                    <a href="/assets/recall" className="w-8 h-8 mr-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors">
                        <ArrowLeft size={16} />
                    </a>
                    <h2 className="font-bold text-000000e0 text-[15px]">Chi tiết thu hồi</h2>
                </div>

                <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm w-full sm:w-auto sm:justify-end justify-start">
                    <button onClick={() => navigate('/assets/add-new-recall')} className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <Plus size={15} className="mr-1 text-gray-400" /> Tạo mới <ChevronDown size={14} className="ml-1" />
                    </button>
                    <button className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <LinkIcon size={15} className="mr-1.5 text-gray-400" /> Liên kết
                    </button>
                    <button onClick={() => navigate('/submit-for-signature/add')} className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <FileSignature size={15} className="mr-1.5 text-gray-400" /> Ký số
                    </button>
                    <Dropdown menu={{ items: rightClickMenuItems }} trigger={['click']}>
                        <button className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                            <FileText size={15} className="mr-1.5 text-gray-400" /> Biểu mẫu <ChevronDown size={14} className="ml-1" />
                        </button>
                    </Dropdown>
                    <button onClick={() => navigate('/assets/recall-edit')} className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <Pencil size={15} className="mr-1.5 text-gray-400" /> Sửa
                    </button>
                </div>
            </div>

            {/* NỘI DUNG CHÍNH 2 CỘT */}
            <div className="mx-auto py-4 grid grid-cols-1 lg:grid-cols-3 gap-5 items-start mt-1">

                {/* CỘT TRÁI */}
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <CollapsibleSection title="Thông tin chung">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2">
                            <InfoItem label="Mã thu hồi" value="BBTH_43" />
                            <InfoItem label="Tên thu hồi" value="Tivi [NTHQ]" />
                            <div>
                                <p className="text-gray-400 text-[12px] mb-1">Người thu hồi</p>
                                <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full text-xs font-semibold text-000000e0">
                                    <span className="w-5 h-5 rounded-full bg-lime-500 flex items-center justify-center mr-1.5 text-[10px]">H</span>
                                    Mr. Huân
                                </div>
                            </div>
                            <InfoItem label="Người sử dụng" value="Lê Quang Hưng" />
                            <InfoItem label="Ngày thu hồi" value="12/08/2026" />
                            <InfoItem label="Địa điểm thu hồi" value="--" />
                            <InfoItem label="--" value="--" />
                            <InfoItem label="Lý do thu hồi" value="--" />
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection title="Danh sách thu hồi tài sản" noPadding>
                        <div className="overflow-x-auto">
                            <Table columns={assetTableColumns} dataSource={assetTableData} pagination={false} size="middle" scroll={{ x: 'max-content' }} />
                        </div>
                    </CollapsibleSection>
                </div>

                {/* CỘT PHẢI */}
                <div className="lg:col-span-1 flex flex-col gap-5">
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
                        <Tabs
                            defaultActiveKey="1"
                            className="px-4 pt-2 custom-antd-tabs"
                            items={[
                                {
                                    key: '1', 
                                    label: 'Thảo luận',
                                    children: (
                                        <div className="py-4 flex flex-col max-h-[420px]">
                                            <div className="flex-1 overflow-y-auto space-y-4 pr-1 mb-3">
                                                {discussions.length === 0 ? (
                                                    <div className="py-6 flex flex-col items-center justify-center text-center">
                                                        <div className="text-gray-300 dark:text-gray-600 mb-2">
                                                            <FileText size={32} strokeWidth={1} />
                                                        </div>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500">Không có thảo luận nào</p>
                                                    </div>
                                                ) : (
                                                    discussions.map((item) => (
                                                        <div key={item.id} className="flex items-start">
                                                            <div className="w-8 h-8 mr-3 rounded-full bg-[#84cc16] text-white flex items-center justify-center text-xs font-bold shrink-0">
                                                                {item.author.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-bold text-gray-800 dark:text-gray-200 mb-1">{item.author}</p>
                                                                <div className="bg-gray-100 dark:bg-gray-700/60 px-3 py-2 rounded-xl text-xs text-gray-800 dark:text-gray-200 inline-block shadow-sm">
                                                                    {item.text}
                                                                </div>
                                                                <p className="text-[10px] text-gray-400 mt-1">{item.time}</p>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {/* Ô input nhập chat phía dưới */}
                                            <div className="relative mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
                                                <Input 
                                                    placeholder="Viết thảo luận..." 
                                                    value={inputMessage}
                                                    onChange={(e) => setInputMessage(e.target.value)}
                                                    onPressEnter={handleSendMessage}
                                                    className="rounded-full pl-4 pr-20 py-2 bg-gray-50 border-gray-200 text-xs" 
                                                />
                                                <div className="absolute right-3 top-4 flex space-x-2 text-gray-400">
                                                    <Paperclip size={14} className="cursor-pointer hover:text-gray-600" />
                                                    <Smile size={14} className="cursor-pointer hover:text-gray-600" />
                                                    <Send size={14} className="cursor-pointer hover:text-[#3e91ab]" onClick={handleSendMessage} />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    key: '2',
                                    label: 'Lịch sử hoạt động',
                                    children: (
                                        <div className="pb-4 px-2">
                                            <div className="relative pl-6 space-y-4 max-h-[420px] overflow-y-auto pr-1">
                                                <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                                                <div className="relative flex items-start">
                                                    <div className="absolute -left-6 top-1 w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center shadow-sm z-10">
                                                        <Plus size={10} />
                                                    </div>
                                                    <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md border border-gray-100 dark:border-gray-700 ml-2">
                                                        <p className="text-[12px] text-gray-800 dark:text-gray-200">
                                                            <span className="font-bold">Mr. Huân</span> đã tạo mới
                                                        </p>
                                                        <p className="text-[10px] text-gray-400 mt-0.5">🕒 21:21 ng 12/08/2026</p>
                                                    </div>
                                                    <div className="w-6 h-6 rounded-full bg-lime-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0 ml-2">H</div>
                                                </div>

                                            </div>
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* CSS TÙY CHỈNH */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-antd-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: #3e91ab !important; font-weight: 600; }
                .custom-antd-tabs .ant-tabs-ink-bar { background: #3e91ab !important; }
                .custom-antd-tabs .ant-tabs-tab:hover { color: #3e91ab !important; }
                
                .ant-dropdown .ant-dropdown-menu-item, 
                .ant-dropdown-menu-submenu-title {
                    padding-top: 8px !important;
                    padding-bottom: 8px !important;
                    display: flex !important;
                    align-items: center !important;
                }
                .ant-dropdown .ant-dropdown-menu-item .lucide,
                .ant-dropdown .ant-dropdown-menu-submenu-title .lucide {
                    margin-right: 8px !important;
                }
            `}} />
        </Layout>
    );
};

export default RecallDetail;